import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  associateWebAuthnCredential,
  resetPassword,
  confirmResetPassword,
  updateUserAttribute,
  confirmUserAttribute,
} from '@aws-amplify/auth'

import { useAuth } from '../context/AuthContext'
import { useTheme } from '../hooks/useTheme'
import { useFontSize } from '../hooks/useFontSize'
import MenuBar from '../components/MenuBar/MenuBar'
import { fetchCities } from '../api/citiesApi'
import { fetchLanguagesConfig } from '../api/languagesApi'
import styles from './AccountSettingsPage.module.css'
import DeleteAccountModal from '../components/DeleteAccountModal/DeleteAccountModal'

/**
 * Language options available in the app.
 * When new translations are added, extend this list — no other changes needed.
 */
const AVAILABLE_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
]

function getSavedSelection(): { lang: string; citySlug: string } | null {
  try {
    const raw = localStorage.getItem('radiri-last-selection')
    return raw ? (JSON.parse(raw) as { lang: string; citySlug: string }) : null
  } catch {
    return null
  }
}

export default function AccountSettingsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const {
    user,
    isLoading,
    signIn,
    signOut,
    updateUserPreferences,
    deleteAccount,
  } = useAuth()
  const [theme, applyTheme] = useTheme()
  const [fontSize, applyFontSize] = useFontSize()

  const [selectedLanguage, setSelectedLanguage] = useState(
    () => i18n.language || 'en'
  )
  const [saving, setSaving] = useState(false)
  const [savedMessage, setSavedMessage] = useState('')
  const [saveError, setSaveError] = useState('')

  const savedSelection = getSavedSelection()
  const [selectedCity, setSelectedCity] = useState(() => savedSelection?.citySlug ?? '')
  const [selectedNewsLang, setSelectedNewsLang] = useState(() => savedSelection?.lang ?? '')

  // Re-read from localStorage once auth finishes loading — this covers the case
  // where the user opens /settings before the auth sync (which writes city/lang to
  // localStorage via applyPreferences) has completed.
  useEffect(() => {
    if (isLoading) return
    const saved = getSavedSelection()
    if (saved?.citySlug) setSelectedCity(saved.citySlug)
    if (saved?.lang) setSelectedNewsLang(saved.lang)
  }, [isLoading])

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => fetchCities(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: allLanguages } = useQuery({
    queryKey: ['languages-config'],
    queryFn: fetchLanguagesConfig,
    staleTime: 60 * 60 * 1000,
  })

  // Languages that exist in at least one city's output_languages
  const availableLanguageCodes = new Set<string>(
    (cities ?? []).flatMap((c) => c.output_languages),
  )
  // Show all languages from config; if output_languages data is available, restrict to
  // those that are actually used — but fall back to the full list so a freshly-created
  // city (with output_languages still []) doesn't produce an empty select.
  const displayNewsLanguages =
    availableLanguageCodes.size > 0
      ? (allLanguages ?? []).filter((l) => availableLanguageCodes.has(l.code))
      : (allLanguages ?? [])

  const [passkeyStatus, setPasskeyStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')
  const [passkeyError, setPasskeyError] = useState('')

  // ── Delete account ──────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // ── In-app password reset (resetPassword + confirmResetPassword) ─────────
  type ResetStep = 'idle' | 'code_sent' | 'done'
  const [resetStep, setResetStep] = useState<ResetStep>('idle')
  const [resetCode, setResetCode] = useState('')
  const [resetNewPassword, setResetNewPassword] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetError, setResetError] = useState('')

  // ── In-app email change (updateUserAttribute + confirmUserAttribute) ──────
  type EmailChangeStep = 'idle' | 'enter_email' | 'code_sent' | 'done'
  const [emailChangeStep, setEmailChangeStep] = useState<EmailChangeStep>('idle')
  const [newEmail, setNewEmail] = useState('')
  const [emailChangeCode, setEmailChangeCode] = useState('')
  const [emailChangeLoading, setEmailChangeLoading] = useState(false)
  const [emailChangeError, setEmailChangeError] = useState('')

  async function handleInitiateEmailChange() {
    if (!user) return
    setEmailChangeLoading(true)
    setEmailChangeError('')
    try {
      await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: newEmail } })
      setEmailChangeStep('code_sent')
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : String(err))
    } finally {
      setEmailChangeLoading(false)
    }
  }

  async function handleConfirmEmailChange() {
    setEmailChangeLoading(true)
    setEmailChangeError('')
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: emailChangeCode })
      setEmailChangeStep('done')
      setNewEmail('')
      setEmailChangeCode('')
    } catch (err) {
      setEmailChangeError(err instanceof Error ? err.message : String(err))
    } finally {
      setEmailChangeLoading(false)
    }
  }

  async function handleInitiateReset() {
    if (!user) return
    setResetLoading(true)
    setResetError('')
    try {
      await resetPassword({ username: user.username })
      setResetStep('code_sent')
    } catch (err) {
      setResetError(err instanceof Error ? err.message : String(err))
    } finally {
      setResetLoading(false)
    }
  }

  async function handleConfirmReset() {
    if (!user) return
    setResetLoading(true)
    setResetError('')
    try {
      await confirmResetPassword({
        username: user.username,
        confirmationCode: resetCode,
        newPassword: resetNewPassword,
      })
      setResetStep('done')
      setResetCode('')
      setResetNewPassword('')
    } catch (err) {
      setResetError(err instanceof Error ? err.message : String(err))
    } finally {
      setResetLoading(false)
    }
  }

  async function handleSetupPasskey() {
    if (typeof PublicKeyCredential === 'undefined') {
      setPasskeyStatus('error')
      setPasskeyError(t('passkey_not_supported'))
      return
    }
    setPasskeyStatus('saving')
    setPasskeyError('')
    try {
      await associateWebAuthnCredential()
      setPasskeyStatus('success')
      setTimeout(() => setPasskeyStatus('idle'), 4000)
    } catch (err) {
      setPasskeyStatus('error')
      // Log every property of the error object for diagnostics
      console.error('[Passkey] Setup failed:', err)
      if (err && typeof err === 'object') {
        console.error('[Passkey] Error details:', JSON.stringify(err, Object.getOwnPropertyNames(err)))
      }
      const errorMessage = err instanceof Error ? err.message : String(err)
      setPasskeyError(errorMessage || t('passkey_error'))
    }
  }

  async function handleDeleteAccount() {
    setIsDeleting(true)
    setDeleteError('')
    try {
      await deleteAccount()
      navigate('/')
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('delete_account_error'))
    } finally {
      setIsDeleting(false)
    }
  }

  async function handleSavePreferences() {
    setSaving(true)
    setSavedMessage('')
    setSaveError('')
    try {
      await updateUserPreferences({
        theme,
        language: selectedLanguage,
        font_size: fontSize,
        city_slug: selectedCity,
        news_language: selectedNewsLang,
      })
      await i18n.changeLanguage(selectedLanguage)
      setSavedMessage(t('settings_saved'))
      setTimeout(() => setSavedMessage(''), 4000)
    } catch {
      setSaveError(t('error_loading'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.pageWrapper}>
      <MenuBar lang="en" city="" />

      <main className={styles.main}>
        <div className={styles.container}>
          <h1 className={styles.pageTitle}>{t('account_settings')}</h1>

          {isLoading && (
            <p className={styles.statusText}>{t('loading')}</p>
          )}

          {!isLoading && !user && (
            <div className={styles.card}>
              <p className={styles.notSignedIn}>{t('sign_in_to_access_settings')}</p>
              <div className={styles.buttonRow}>
                <button className={styles.primaryBtn} onClick={signIn}>
                  {t('sign_in')}
                </button>
              </div>
            </div>
          )}

          {!isLoading && user && (
            <>
              {/* ── Username ──────────────────────────────────────────── */}
              <p className={styles.usernameDisplay}>@{user.username}</p>

              {/* ── Preferences ───────────────────────────────────────── */}
              <section className={styles.card}>
                <h2 className={styles.sectionTitle}>{t('preferences')}</h2>

                <div className={styles.field}>
                  <span className={styles.label}>{t('theme')}</span>
                  <div className={styles.themeRow}>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${theme === 'light' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyTheme('light')}
                      aria-pressed={theme === 'light'}
                    >
                      ☀️ {t('theme_light')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${theme === 'dark' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyTheme('dark')}
                      aria-pressed={theme === 'dark'}
                    >
                      🌙 {t('theme_dark')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${theme === 'paper' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyTheme('paper')}
                      aria-pressed={theme === 'paper'}
                    >
                      📰 {t('theme_paper')}
                    </button>
                  </div>
                </div>

                <div className={styles.field}>
                  <span className={styles.label}>{t('font_size')}</span>
                  <div className={styles.themeRow}>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${fontSize === 'small' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyFontSize('small')}
                      aria-pressed={fontSize === 'small'}
                    >
                      {t('font_size_small')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${fontSize === 'medium' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyFontSize('medium')}
                      aria-pressed={fontSize === 'medium'}
                    >
                      {t('font_size_medium')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.themeOption} ${fontSize === 'large' ? styles.themeOptionActive : ''}`}
                      onClick={() => applyFontSize('large')}
                      aria-pressed={fontSize === 'large'}
                    >
                      {t('font_size_large')}
                    </button>
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="lang-select">
                    {t('language')}
                  </label>
                  <select
                    id="lang-select"
                    className={styles.select}
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                    {AVAILABLE_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="city-select">
                    {t('city')}
                  </label>
                  <select
                    id="city-select"
                    className={styles.select}
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    <option value="">—</option>
                    {(cities ?? []).map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="news-lang-select">
                    {t('news_language')}
                  </label>
                  <select
                    id="news-lang-select"
                    className={styles.select}
                    value={selectedNewsLang}
                    onChange={(e) => setSelectedNewsLang(e.target.value)}
                  >
                    <option value="">—</option>
                    {displayNewsLanguages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.code} {l.flag}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.saveRow}>
                  {savedMessage && (
                    <span className={styles.savedMsg}>{savedMessage}</span>
                  )}
                  {saveError && (
                    <span className={styles.errorMsg}>{saveError}</span>
                  )}
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={() => void handleSavePreferences()}
                    disabled={saving}
                  >
                    {saving ? t('loading') : t('save_preferences')}
                  </button>
                </div>
              </section>

              {/* ── Account actions (Hosted UI) ────────────────────────── */}
              <section className={styles.card}>
                <h2 className={styles.sectionTitle}>{t('account_actions')}</h2>
                <ul className={styles.actionList}>
                  <li>
                    {resetStep === 'idle' && (
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() => void handleInitiateReset()}
                        disabled={resetLoading}
                      >
                        {resetLoading ? t('loading') : t('reset_password')}
                      </button>
                    )}
                    {resetStep === 'code_sent' && (
                      <div className={styles.resetForm}>
                        <p className={styles.resetHint}>{t('reset_password_code_sent')}</p>
                        <input
                          type="text"
                          className={styles.textInput}
                          placeholder={t('reset_password_code_label')}
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)}
                          autoComplete="one-time-code"
                        />
                        <input
                          type="password"
                          className={styles.textInput}
                          placeholder={t('reset_password_new_password_label')}
                          value={resetNewPassword}
                          onChange={(e) => setResetNewPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        {resetError && <span className={styles.errorMsg}>{resetError}</span>}
                        <div className={styles.buttonRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => void handleConfirmReset()}
                            disabled={resetLoading || !resetCode || !resetNewPassword}
                          >
                            {resetLoading ? t('loading') : t('reset_password_confirm')}
                          </button>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => { setResetStep('idle'); setResetError('') }}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {resetStep === 'done' && (
                      <span className={styles.savedMsg}>{t('reset_password_success')}</span>
                    )}
                    {resetStep === 'idle' && resetError && (
                      <span className={styles.errorMsg}>{resetError}</span>
                    )}
                  </li>
                  <li>
                    {emailChangeStep === 'idle' && (
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() => setEmailChangeStep('enter_email')}
                      >
                        {t('change_email')}
                      </button>
                    )}
                    {emailChangeStep === 'enter_email' && (
                      <div className={styles.resetForm}>
                        <p className={styles.resetHint}>{t('change_email_enter_hint')}</p>
                        <input
                          type="email"
                          className={styles.textInput}
                          placeholder={t('change_email_email_label')}
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          autoComplete="email"
                        />
                        {emailChangeError && <span className={styles.errorMsg}>{emailChangeError}</span>}
                        <div className={styles.buttonRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => void handleInitiateEmailChange()}
                            disabled={emailChangeLoading || !newEmail}
                          >
                            {emailChangeLoading ? t('loading') : t('change_email_confirm')}
                          </button>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => { setEmailChangeStep('idle'); setEmailChangeError(''); setNewEmail('') }}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {emailChangeStep === 'code_sent' && (
                      <div className={styles.resetForm}>
                        <p className={styles.resetHint}>{t('change_email_code_sent')}</p>
                        <input
                          type="text"
                          className={styles.textInput}
                          placeholder={t('change_email_code_label')}
                          value={emailChangeCode}
                          onChange={(e) => setEmailChangeCode(e.target.value)}
                          autoComplete="one-time-code"
                        />
                        {emailChangeError && <span className={styles.errorMsg}>{emailChangeError}</span>}
                        <div className={styles.buttonRow}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => void handleConfirmEmailChange()}
                            disabled={emailChangeLoading || !emailChangeCode}
                          >
                            {emailChangeLoading ? t('loading') : t('change_email_confirm')}
                          </button>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => { setEmailChangeStep('idle'); setEmailChangeError(''); setEmailChangeCode('') }}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {emailChangeStep === 'done' && (
                      <span className={styles.savedMsg}>{t('change_email_success')}</span>
                    )}
                  </li>
                  <li>
                    <button
                      type="button"
                      className={styles.actionButton}
                      onClick={() => void handleSetupPasskey()}
                      disabled={passkeyStatus === 'saving'}
                    >
                      {passkeyStatus === 'saving' ? t('loading') : t('setup_passkey')}
                    </button>
                    {passkeyStatus === 'success' && (
                      <span className={styles.savedMsg}>{t('passkey_success')}</span>
                    )}
                    {passkeyStatus === 'error' && (
                      <span className={styles.errorMsg}>{passkeyError}</span>
                    )}
                  </li>
                </ul>
              </section>

              {/* ── Sign out ──────────────────────────────────────────── */}
              <section className={styles.card}>
                <button
                  type="button"
                  className={styles.signOutBtn}
                  onClick={() => void signOut()}
                >
                  {t('sign_out')}
                </button>
              </section>

              {/* ── Danger zone ───────────────────────────────────────── */}
              <section className={styles.dangerZone}>
                <h2 className={styles.dangerZoneTitle}>{t('delete_account_danger_zone')}</h2>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  title={t('delete_account_tooltip')}
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t('delete_account')}
                </button>
              </section>
            </>
          )}

          <Link to="/" className={styles.backLink}>
            {t('back_to_home')}
          </Link>
        </div>
      </main>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onConfirm={() => void handleDeleteAccount()}
        onCancel={() => { setShowDeleteModal(false); setDeleteError('') }}
        isDeleting={isDeleting}
        error={deleteError}
      />
    </div>
  )
}
